import { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calculator, AlertCircle, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Props {
  dealId: string;
  language: 'en' | 'fr';
}

const t = {
  en: {
    title: 'DSCR Evaluator — Staff',
    subtitle: 'Adjust deal components to reach a target DSCR of 1.3x before publication',
    loadingDeal: 'Loading deal financials...',
    earningsBase: 'Earnings Base',
    ebitda: 'EBITDA',
    sde: 'SDE (Owner Cash Flow)',
    askingPrice: 'Asking Price',
    adjustedPrice: 'Adjusted Price',
    ownerSalaryAddback: 'Owner Salary Addback',
    ownerSalaryDesc: 'Add back all or part of the owner\'s salary to the earnings base — a new owner may not draw the same compensation.',
    includeAt: 'Include at (%)',
    adjustedEarnings: 'Adjusted Earnings (used for DSCR)',
    components: 'Components to exclude / reduce',
    realEstate: 'Real Estate',
    inventory: 'Inventory',
    ffe: 'Furniture, Fixtures & Equipment',
    accountsReceivable: 'Accounts Receivable',
    exclude: 'Exclude entirely',
    reduceBy: 'Reduce by (%)',
    financingAssumptions: 'Financing Assumptions',
    downPayment: 'Down Payment (%)',
    interestRate: 'Interest Rate (%)',
    termYears: 'Loan Term (years)',
    annuity: 'Conventional',
    sba: 'SBA',
    results: 'Results',
    loanAmount: 'Loan Amount',
    annualDebtService: 'Annual Debt Service',
    dscr: 'Estimated DSCR',
    dscrGood: 'Target reached (≥1.3x)',
    dscrWarning: 'Marginal coverage',
    dscrBad: 'Insufficient coverage',
    gapToTarget: 'Reduction still needed to reach 1.3x',
    save: 'Save analysis to deal (for lender impact report)',
    saved: 'DSCR analysis saved',
    saveError: 'Failed to save analysis',
    disclaimer: 'Internal staff tool. This estimate uses generic financing assumptions — actual lender terms may differ.',
    noDataWarning: 'No financial data found for this deal — check that the seller submitted step 6 (Financials) and step 4 (Assets).',
  },
  fr: {
    title: 'Évaluateur DSCR — Staff',
    subtitle: 'Ajustez les composantes du deal pour atteindre un DSCR cible de 1,3x avant publication',
    loadingDeal: 'Chargement des données financières du deal...',
    earningsBase: 'Base de revenu',
    ebitda: 'EBITDA',
    sde: 'SDE (cash-flow du propriétaire)',
    askingPrice: 'Prix demandé',
    adjustedPrice: 'Prix ajusté',
    ownerSalaryAddback: 'Retraitement du salaire du propriétaire',
    ownerSalaryDesc: "Ajouter tout ou partie du salaire du propriétaire à la base de revenu — un nouveau propriétaire pourrait ne pas se verser la même rémunération.",
    includeAt: 'Inclure à (%)',
    adjustedEarnings: 'Revenu ajusté (utilisé pour le DSCR)',
    components: 'Composantes à exclure / réduire',
    realEstate: 'Immobilier',
    inventory: 'Inventaire',
    ffe: 'Mobilier, agencements et équipement',
    accountsReceivable: 'Comptes clients',
    exclude: 'Exclure complètement',
    reduceBy: 'Réduire de (%)',
    financingAssumptions: 'Hypothèses de financement',
    downPayment: 'Mise de fonds (%)',
    interestRate: "Taux d'intérêt (%)",
    termYears: 'Durée du prêt (années)',
    annuity: 'Conventionnel',
    sba: 'SBA',
    results: 'Résultats',
    loanAmount: 'Montant du prêt',
    annualDebtService: 'Service de dette annuel',
    dscr: 'DSCR estimé',
    dscrGood: 'Objectif atteint (≥1,3x)',
    dscrWarning: 'Couverture marginale',
    dscrBad: 'Couverture insuffisante',
    gapToTarget: 'Réduction encore nécessaire pour atteindre 1,3x',
    save: "Enregistrer l'analyse sur le deal (pour le rapport d'impact prêteurs)",
    saved: 'Analyse DSCR enregistrée',
    saveError: "Échec de l'enregistrement",
    disclaimer: "Outil interne staff. Cette estimation utilise des hypothèses de financement génériques — les conditions réelles du prêteur peuvent différer.",
    noDataWarning: "Aucune donnée financière trouvée pour ce deal — vérifiez que le vendeur a bien complété l'étape 6 (Financiers) et l'étape 4 (Actifs).",
  },
};

const DSCR_TARGET = 1.3;

// AJOUTÉ — mêmes hypothèses conditionnelles au pays du deal que dans
// StepDocuments.tsx (getFinancingAssumptions) — dupliquée ici plutôt que
// partagée via un import commun, pour ne pas introduire de dépendance
// entre un composant frontend public (formulaire) et un composant staff
// interne, mais les valeurs doivent rester identiques dans les deux
// fichiers si l'une est mise à jour.
//   - Canada : 75% LTV, 7,0%, 10 ans
//   - États-Unis : 80% LTV, 9,0%, 10 ans
//   - Autre / non reconnu : repli sur l'hypothèse américaine
function getFinancingAssumptions(locationCountry: string | undefined) {
  const c = (locationCountry || '').toLowerCase();
  if (c.includes('canada')) {
    return { ltv: 75, rate: 7.0, termYears: 10, region: 'Canada' as const };
  }
  if (c.includes('united states') || c.includes('usa') || c.includes('états-unis') || c.includes('etats-unis')) {
    return { ltv: 80, rate: 9.0, termYears: 10, region: 'United States' as const };
  }
  return { ltv: 80, rate: 9.0, termYears: 10, region: 'default' as const };
}

function calcAnnuityPayment(principal: number, annualRate: number, termYears: number): number {
  if (principal <= 0 || termYears <= 0) return 0;
  if (annualRate === 0) return principal / (termYears * 12);
  const r = annualRate / 100 / 12;
  const n = termYears * 12;
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
}

function getDscrColor(dscr: number): string {
  if (dscr >= DSCR_TARGET) return 'bg-green-100 text-green-800';
  if (dscr >= 1.0) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
}

function getDscrLabel(dscr: number, labels: typeof t.en): string {
  if (dscr >= DSCR_TARGET) return labels.dscrGood;
  if (dscr >= 1.0) return labels.dscrWarning;
  return labels.dscrBad;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

export function StaffDscrEvaluator({ dealId, language }: Props) {
  const labels = t[language];
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasAnyData, setHasAnyData] = useState(true);

  // Real deal financials, loaded from Firestore
  const [askingPrice, setAskingPrice] = useState(0);
  const [ebitda, setEbitda] = useState(0);
  const [sde, setSde] = useState(0);
  const [realEstateValue, setRealEstateValue] = useState(0);
  const [inventoryValue, setInventoryValue] = useState(0);
  const [ffeValue, setFfeValue] = useState(0);
  const [accountsReceivable, setAccountsReceivable] = useState(0);
  const [ownerSalary, setOwnerSalary] = useState(0);

  // Earnings base toggle
  const [earningsBase, setEarningsBase] = useState<'ebitda' | 'sde'>('ebitda');

  // Owner salary addback — separate from the price-reduction components below:
  // this ADDS to earnings (numerator) rather than reducing the loan amount.
  const [ownerSalaryIncludePct, setOwnerSalaryIncludePct] = useState(0);

  // Component exclusion / reduction toggles
  const [excludeRealEstate, setExcludeRealEstate] = useState(false);
  const [reduceRealEstatePct, setReduceRealEstatePct] = useState(0);
  const [excludeInventory, setExcludeInventory] = useState(false);
  const [reduceInventoryPct, setReduceInventoryPct] = useState(0);
  const [excludeFfe, setExcludeFfe] = useState(false);
  const [reduceFfePct, setReduceFfePct] = useState(0);
  const [excludeAR, setExcludeAR] = useState(false);
  const [reduceARPct, setReduceARPct] = useState(0);

  // Financing assumptions — valeurs par défaut au chargement, écrasées
  // dès que le deal est chargé par des hypothèses conditionnelles au pays
  // (voir getFinancingAssumptions ci-dessus et le useEffect de chargement
  // plus bas), et librement modifiables par le staff ensuite au cas par
  // cas. Ces valeurs initiales (avant chargement) restent celles par
  // défaut des États-Unis — le cas le mieux documenté — et ne sont
  // jamais visibles à l'écran puisque isLoading masque les champs tant
  // que le deal (et donc le pays réel) n'est pas encore chargé.
  const [downPayment, setDownPayment] = useState(20);
  const [interestRate, setInterestRate] = useState(9.0);
  const [termYears, setTermYears] = useState(10);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const snap = await getDoc(doc(db, 'dealSubmissions', dealId));
        if (!snap.exists() || cancelled) return;
        const d = snap.data() as any;

        const f = d.rawFormData || {};
        const financials = f.financials || d.financials || {};
        const assets = f.assets || d.assets || {};
        const realEstate = f.realEstate || d.realEstate || {};
        const operations = f.operations || d.operations || {};

        const loadedAskingPrice = financials.includeRealEstateInPrice
          ? (financials.totalAskingPriceWithRealEstate || financials.askingPrice || d.askingPrice || 0)
          : (financials.askingPrice || d.askingPrice || 0);
        const loadedEbitda = financials.ebitdaYear3 || financials.ebitdaYear1 || d.ebitdaYear3 || d.ebitdaYear1 || 0;
        const loadedSde = financials.cashFlowEquity || d.cashFlowEquity || 0;
        const loadedRealEstate = realEstate.propertyValue || d.realEstateValue || d.propertyValue || 0;
        const loadedInventory = assets.inventoryValue || d.inventoryValue || 0;
        const loadedFfe = assets.ffeValue || d.ffeValue || 0;
        const loadedAR = assets.accountsReceivable || d.accountsReceivable || 0;
        const loadedOwnerSalary = operations.ownerAnnualSalary || d.ownerAnnualSalary || 0;
        // AJOUTÉ — hypothèses de financement par défaut conditionnelles
        // au pays du deal (voir le même correctif dans StepDocuments.tsx,
        // getFinancingAssumptions) — chargées une seule fois ici au
        // montage, restent librement modifiables par le staff ensuite.
        const loadedLocationCountry = f.general?.locationCountry || d.locationCountry || '';

        setAskingPrice(loadedAskingPrice);
        setEbitda(loadedEbitda);
        setSde(loadedSde);
        setRealEstateValue(loadedRealEstate);
        setInventoryValue(loadedInventory);
        setFfeValue(loadedFfe);
        setAccountsReceivable(loadedAR);
        setOwnerSalary(loadedOwnerSalary);

        const assumptions = getFinancingAssumptions(loadedLocationCountry);
        setDownPayment(100 - assumptions.ltv);
        setInterestRate(assumptions.rate);
        setTermYears(assumptions.termYears);

        setHasAnyData(
          loadedAskingPrice > 0 || loadedEbitda > 0 || loadedSde > 0
        );
      } catch (error) {
        console.error('[StaffDscrEvaluator] Failed to load deal:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dealId]);

  const componentReduction = (value: number, excluded: boolean, reducePct: number) => {
    if (excluded) return value;
    return value * (reducePct / 100);
  };

  const totalReduction = useMemo(() => {
    return (
      componentReduction(realEstateValue, excludeRealEstate, reduceRealEstatePct) +
      componentReduction(inventoryValue, excludeInventory, reduceInventoryPct) +
      componentReduction(ffeValue, excludeFfe, reduceFfePct) +
      componentReduction(accountsReceivable, excludeAR, reduceARPct)
    );
  }, [
    realEstateValue, excludeRealEstate, reduceRealEstatePct,
    inventoryValue, excludeInventory, reduceInventoryPct,
    ffeValue, excludeFfe, reduceFfePct,
    accountsReceivable, excludeAR, reduceARPct,
  ]);

  const adjustedPrice = Math.max(askingPrice - totalReduction, 0);
  const baseEarnings = earningsBase === 'ebitda' ? ebitda : sde;
  const ownerSalaryAddback = ownerSalary * (ownerSalaryIncludePct / 100);
  const earnings = baseEarnings + ownerSalaryAddback;

  const result = useMemo(() => {
    const loan = adjustedPrice * (1 - downPayment / 100);
    const monthly = calcAnnuityPayment(loan, interestRate, termYears);
    const annual = monthly * 12;
    const dscr = annual > 0 ? earnings / annual : 0;
    return { loan, annual, dscr };
  }, [adjustedPrice, downPayment, interestRate, termYears, earnings]);

  // How much more reduction (in $) is needed to reach the 1.3x target,
  // solved by finding the annual debt service that would produce DSCR = target,
  // then back-solving the loan principal via the annuity formula's inverse ratio.
  const gapToTarget = useMemo(() => {
    if (result.dscr >= DSCR_TARGET || earnings <= 0) return 0;
    const targetAnnualService = earnings / DSCR_TARGET;
    if (result.annual <= 0) return 0;
    const targetLoan = result.loan * (targetAnnualService / result.annual);
    const targetAdjustedPrice = targetLoan / (1 - downPayment / 100);
    return Math.max(adjustedPrice - targetAdjustedPrice, 0);
  }, [result, earnings, adjustedPrice, downPayment]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await setDoc(doc(db, 'dealSubmissions', dealId), {
        dscrAnalysis: {
          earningsBase,
          baseEarnings,
          ownerSalary,
          ownerSalaryIncludePct,
          ownerSalaryAddback,
          earnings,
          askingPrice,
          adjustedPrice,
          exclusions: {
            realEstate: { excluded: excludeRealEstate, reducePct: reduceRealEstatePct, value: realEstateValue },
            inventory: { excluded: excludeInventory, reducePct: reduceInventoryPct, value: inventoryValue },
            ffe: { excluded: excludeFfe, reducePct: reduceFfePct, value: ffeValue },
            accountsReceivable: { excluded: excludeAR, reducePct: reduceARPct, value: accountsReceivable },
          },
          financingAssumptions: { downPayment, interestRate, termYears },
          loanAmount: result.loan,
          annualDebtService: result.annual,
          dscr: result.dscr,
          targetDscr: DSCR_TARGET,
          calculatedAt: serverTimestamp(),
        },
      }, { merge: true });
      toast({ description: labels.saved });
    } catch (error: any) {
      console.error('[StaffDscrEvaluator] Save failed:', error);
      toast({ title: 'Error', description: labels.saveError, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const numInput = (val: number, setter: (v: number) => void, label: string, step = 1) => (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        type="number"
        step={step}
        value={val}
        onChange={(e) => setter(parseFloat(e.target.value) || 0)}
        className="h-8 text-sm"
      />
    </div>
  );

  const exclusionRow = (
    label: string,
    value: number,
    excluded: boolean,
    setExcluded: (v: boolean) => void,
    reducePct: number,
    setReducePct: (v: number) => void,
  ) => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end py-2 border-b last:border-b-0">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{fmt(value)}</p>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox checked={excluded} onCheckedChange={(v) => setExcluded(!!v)} />
        <Label className="text-xs">{labels.exclude}</Label>
      </div>
      <div className={excluded ? 'opacity-40 pointer-events-none' : ''}>
        {numInput(reducePct, setReducePct, labels.reduceBy)}
      </div>
      <div className="text-sm text-right text-muted-foreground">
        − {fmt(componentReduction(value, excluded, reducePct))}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">{labels.loadingDeal}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-[#1D314D] text-white">
          <Calculator className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#1D314D]">{labels.title}</h2>
          <p className="text-sm text-muted-foreground">{labels.subtitle}</p>
        </div>
      </div>

      {!hasAnyData && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-900">{labels.noDataWarning}</p>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{labels.earningsBase}</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={earningsBase} onValueChange={(v) => setEarningsBase(v as 'ebitda' | 'sde')}>
            <TabsList className="grid grid-cols-2 w-full max-w-xs">
              <TabsTrigger value="ebitda">{labels.ebitda}</TabsTrigger>
              <TabsTrigger value="sde">{labels.sde}</TabsTrigger>
            </TabsList>
          </Tabs>
          <p className="text-lg font-semibold mt-3">{fmt(baseEarnings)}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{labels.ownerSalaryAddback}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-3">{labels.ownerSalaryDesc}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-xs text-muted-foreground">{labels.title === t.fr.title ? 'Salaire annuel déclaré' : 'Declared annual salary'}</Label>
              <Input
                type="number"
                value={ownerSalary}
                onChange={(e) => setOwnerSalary(parseFloat(e.target.value) || 0)}
                className="h-8 text-sm mt-1"
              />
            </div>
            {numInput(ownerSalaryIncludePct, setOwnerSalaryIncludePct, labels.includeAt)}
            <div className="text-sm text-right text-muted-foreground">
              + {fmt(ownerSalaryAddback)}
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 mt-3 border-t">
            <span className="text-sm font-semibold">{labels.adjustedEarnings}</span>
            <span className="text-sm font-semibold">{fmt(earnings)}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{labels.components}</CardTitle>
        </CardHeader>
        <CardContent>
          {exclusionRow(labels.realEstate, realEstateValue, excludeRealEstate, setExcludeRealEstate, reduceRealEstatePct, setReduceRealEstatePct)}
          {exclusionRow(labels.inventory, inventoryValue, excludeInventory, setExcludeInventory, reduceInventoryPct, setReduceInventoryPct)}
          {exclusionRow(labels.ffe, ffeValue, excludeFfe, setExcludeFfe, reduceFfePct, setReduceFfePct)}
          {exclusionRow(labels.accountsReceivable, accountsReceivable, excludeAR, setExcludeAR, reduceARPct, setReduceARPct)}
          <div className="flex items-center justify-between pt-3 mt-2 border-t">
            <span className="text-sm text-muted-foreground">{labels.askingPrice}</span>
            <span className="text-sm">{fmt(askingPrice)}</span>
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-sm font-semibold">{labels.adjustedPrice}</span>
            <span className="text-sm font-semibold">{fmt(adjustedPrice)}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{labels.financingAssumptions}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {numInput(downPayment, setDownPayment, labels.downPayment)}
          {numInput(interestRate, setInterestRate, labels.interestRate, 0.1)}
          {numInput(termYears, setTermYears, labels.termYears)}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{labels.results}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-sm text-muted-foreground">{labels.loanAmount}</span>
            <span className="font-semibold text-sm">{fmt(result.loan)}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-sm text-muted-foreground">{labels.annualDebtService}</span>
            <span className="font-semibold text-sm">{fmt(result.annual)}</span>
          </div>
          <div className="flex items-center justify-between pt-3">
            <span className="text-sm font-medium">{labels.dscr}</span>
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg">{result.dscr.toFixed(2)}x</span>
              <Badge variant="outline" className={getDscrColor(result.dscr)}>
                {getDscrLabel(result.dscr, labels)}
              </Badge>
            </div>
          </div>
          {gapToTarget > 0 && (
            <div className="flex items-center justify-between pt-2 text-sm text-amber-700">
              <span>{labels.gapToTarget}</span>
              <span className="font-semibold">{fmt(gapToTarget)}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={isSaving} className="w-full">
        <Save className="h-4 w-4 mr-2" />
        {labels.save}
      </Button>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
        <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-900">{labels.disclaimer}</p>
      </div>
    </div>
  );
}